import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import i18next from 'i18next';

import { LiveHelper } from '../../../util/live-helper';
import { setPlayerCurrentPosition } from '../../../actions/playerActions';
import { joinClassNames } from '../../../util/util';

import {
    TopControlsAccountTitle,
    TopControlsContainer,
    TopControlsMediaTitle,
    TopControlsPlaybackTitle,
    TopControlsPlaybackTitleWrapper,
    TopControlsProps
} from './TopControls.styled';

export const StoryTopControl = ({ className, settings, isMobile, queryParams }: TopControlsProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { currentPosition, duration: durationReducer, isPlaying } = useSelector((state) => (state as any).playerReducer);
    const { mediaName, embedShareSettings, isMediaLive, mediasData, currentMediaId} = useSelector(
        (state) => (state as any).configurationReducer
    );

    const timelineWrapperRef = useRef<HTMLElement | null>();
    const [indicatorPosition, setIndicatorPosition] = useState(0);
    const [overShowingTime, setOverShowingTime] = useState<boolean>(false);

    const currentPlayerId = useMemo(() => `player-${currentMediaId}`, [currentMediaId]);
    const solidColor = settings?.controls?.solidBackgroundTopColor ?? '#00000';
    const solidColorStyle = settings?.controls.showSolidBackgroundTopControl
        ? {
                backgroundColor: tinycolor({
                    ...tinycolor(solidColor).toRgb(),
                    a: Number(settings?.controls.solidBackgroundTopControlOpacity ?? 100) / 100,
                }).toRgbString(),
            }
        : undefined;
    const { primaryColor } = useMemo(() => {
        const playerColor = settings?.playerStyling?.primaryColor ?? '#126cfc';
        return {
            color: playerColor,
            primaryColor: { backgroundColor: playerColor },
        };
    }, [settings?.playerStyling?.primaryColor]);
    const progressTranslate = useMemo(
        () => ({
            width: `${indicatorPosition}%`,
        }),
        [indicatorPosition]
    );
    const currentMedia = useMemo(
        () => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
        [mediasData, currentMediaId]
    );
	const accountName = useMemo(() => {
		if (!settings?.controls?.accountName) {
			return '';
		}
		if (typeof settings?.controls?.accountName === 'string') {
			return settings?.controls?.accountName;
		}

		const currentLanguage = i18next.languages[0] === 'no' ? 'nb' : i18next.languages[0] === 'dk' ? 'da' : i18next.languages[0];
		return settings?.controls?.accountName[currentLanguage] ?? settings?.controls?.accountName['all'];
	}, [settings?.controls?.accountName]);

	const isShowingTitle = settings?.controls?.showTitleDuringPlayback
		? settings?.controls?.showTitleDuringPlayback
		: !overShowingTime
			? true
			: !isPlaying;
	const isShowingAccName = settings?.controls?.showAccountNameDuringPlayback
		? settings?.controls?.showAccountNameDuringPlayback
		: !overShowingTime
			? true
			: !isPlaying;
    const duration = currentMedia?.duration ?? durationReducer;

	const isInfoExcludedOnThisHost = (option: any, domainList: any) => {
		let isInfoHidden = false;

		const titleDomainList = (domainList ?? '').split(',');

		if (option && titleDomainList.length !== 0) {
			const url = embedShareSettings?.origin ?? '';
			isInfoHidden = titleDomainList.some((domain: any) => domain && url.includes(domain.trim()));
		}

		return isInfoHidden;
	};

	const mediaTitleHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings?.controls?.showExcludingTitleOnDomains,
		settings?.controls?.excludingTitleOnDomains
	);

	const companyNameHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings?.controls?.showExcludingAccountNameOnDomains,
		settings?.controls?.excludingAccountNameOnDomains
	);

    const getPercentageBaseOnCurrentPosition = useCallback(
        (unitPer = 100) => {
            if (typeof currentPosition === "string" || currentPosition < 0 || LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
                return 100;
            }
            return (Math.abs(currentPosition) / Math.abs(duration)) * unitPer;
        },
        [currentPosition, duration, isMediaLive]
    );

    const handleClickOnProgressBar = useCallback(
        (event: React.MouseEvent<HTMLElement> | MouseEvent) => {
            event.stopPropagation();
            const progressBar = ((event.target as any).classList.contains('top-control--timeline') ? event.target : timelineWrapperRef?.current) as HTMLElement;
			const bcr = progressBar.getBoundingClientRect();
			const xTiming = Math.round(((event.clientX - bcr.left) / bcr.width) * duration * 1000) / 1000;
			const percentage = (Math.abs(xTiming) / Math.abs(duration)) * 100;

            dispatch(setPlayerCurrentPosition(xTiming));
            (window as any).GoBrain.widgets(currentPlayerId)?.position?.(xTiming);
            setIndicatorPosition(percentage);
        },
        [currentPlayerId, dispatch, duration]
    );

    useEffect(() => {
        setIndicatorPosition(getPercentageBaseOnCurrentPosition());
    }, [currentMedia?.duration, currentPosition, duration, durationReducer, getPercentageBaseOnCurrentPosition]);

    useEffect(() => {
        const clearTimeId = setTimeout(() => {
            setOverShowingTime(true);
        }, 3000);

        return () => {
            clearTimeout(clearTimeId);
        };
    }, []);

    return <TopControlsContainer
        story
        overlay={queryParams?.inline === "off"}
        className={joinClassNames(queryParams?.inline === "off" ? 'top-control--story-overlay top-control--not-inline' : '', "top-control--story", className)}
    >
        <div ref={r => timelineWrapperRef.current = r} className="top-control__timeline-wrapper">
            <div role='button' onClick={handleClickOnProgressBar} className='top-control__timeline-button'>
                <div className={"top-control__timeline"} onClick={handleClickOnProgressBar}>
                    <span
                        className="top-control__timeline--active"
                        style={isMediaLive && indicatorPosition === 0 ? { ...primaryColor } : { ...progressTranslate, ...primaryColor }}
                    />
            </div>
            </div>
        </div>

        <div className="top-control__title-wrapper">
            {settings?.controls.enabled &&
                settings?.controls?.showLogotype &&
                settings?.controls?.logotypeUrl &&
                !companyNameHiddenOnThisHost && (
                    <img
                        style={{
                            opacity: `${isShowingAccName ? 1 : 0}`,
                            transition: 'all 0.5s',
                        }}
                        aria-hidden={true}
                        src={settings?.controls?.logotypeUrl}
                    />
                )}
            <TopControlsPlaybackTitleWrapper>
                {settings?.controls.enabled && settings?.controls?.showTitle && !mediaTitleHiddenOnThisHost && (
                    <TopControlsPlaybackTitle
                        className="topControlbar__control--title"
                        aria-hidden={!isShowingTitle}
                        aria-label={`${t('ARIA_LABEL_MEDIA_TITLE')}${mediaName || currentMedia?.mediaName}`}
                        style={{
                            ...solidColorStyle,
                            width: 'fit-content',
                            borderRadius: '4px',
                            opacity: `${isShowingTitle ? 1 : 0}`,
                            transition: 'all 0.5s'
                        }}
                        role="heading"
                        aria-level={2}
                    >
                        <TopControlsMediaTitle aria-hidden="true" isMobile={isMobile}>
                            {mediaName || currentMedia?.mediaName}
                        </TopControlsMediaTitle>
                    </TopControlsPlaybackTitle>
                )}
                {settings?.controls.enabled && settings?.controls?.showAccountName && accountName && !companyNameHiddenOnThisHost && (
                    <TopControlsPlaybackTitle
                        className="topControlbar__control--accountName"
                        aria-label={`${t('ARIA_LABEL_PUBLISHER')}${accountName}`}
                        aria-hidden={!isShowingAccName}
                        style={{
                            ...solidColorStyle,
                            width: 'fit-content',
                            borderRadius: '4px',
                            opacity: `${isShowingAccName ? 1 : 0}`,
                            transition: 'all 0.5s'
                        }}
                        role="heading"
                        aria-level={2}
                    >
                        <TopControlsAccountTitle aria-hidden="true" isMobile={isMobile}>
                            {accountName}
                        </TopControlsAccountTitle>
                    </TopControlsPlaybackTitle>
                )}
            </TopControlsPlaybackTitleWrapper>
        </div>
    </TopControlsContainer>
}